import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { template } from "lodash";
import {
    Button,
    ButtonToolbar,
    Form,
    FormInstance,
    InputPicker,
    Message,
    Schema,
    toaster,
} from "rsuite";

import ModalContainer from "components/Common/Modal/ModalContainer";

import constants from "utils/constants";

import { getErrorMessage } from "api/defaults";
import {
    loadEmailTemplateVariables,
    loadLocales,
} from "api/deliverables/email-templates-api";

import { AppDispatch, RootState } from "store/store";

import { setErrorResponseMessage } from "store/common/initialization/initialization-actions";
import { setModalLoaderVisibility } from "store/common/modal-window/modal-window-slices";

import {
    IEmailTemplate,
    IEmailTemplateVariable,
} from "store/deliverables/email-templates/email-templates-types";
import { setManageTemplateOpened } from "store/deliverables/email-templates/email-templates-actions";

import Textarea from "components/Common/Textarea/Textarea";

import css from "./ManageEmailTemplate.module.css";

interface ComponentProps {
    existingTemplates: IEmailTemplate[];
    refreshTemplates: () => void;
}

const useAppDispatch = () => useDispatch<AppDispatch>();

const ManageEmailTemplate: React.FC<ComponentProps> = ({
    existingTemplates,
    refreshTemplates,
}: ComponentProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const templateDetails = useSelector(
        (state: RootState) => state.emailTemplatesReducer.templateDetails
    );
    const isOpened = useSelector(
        (state: RootState) => state.emailTemplatesReducer.isManageTemplateOpened
    );
    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);
    const [emailTemplate, setEmailTemplate] = useState<IEmailTemplate>(
        templateDetails.emailTemplate
    );
    const [templateVariables, setTemplateVariables] =
        useState<IEmailTemplateVariable[]>();
    const [locales, setLocales] = useState<string[]>([]);
    const [isMetadataFetched, setMetadataFetched] = useState<boolean>(false);

    const fetchMetadata = () => {
        dispatch(setModalLoaderVisibility(true));
        Promise.all([
            loadEmailTemplateVariables().then(([result, status]) => {
                if (result && status === constants.statusCode.OK) {
                    setTemplateVariables(result);
                } else {
                    setErrorResponseMessage(
                        getErrorMessage(String(result), status)
                    );
                }
            }),
            loadLocales().then(([result, status]) => {
                if (result && status === constants.statusCode.OK) {
                    setLocales(result);
                } else {
                    setErrorResponseMessage(
                        getErrorMessage(String(result), status)
                    );
                }
            }),
        ]).then(() => {
            dispatch(setModalLoaderVisibility(false));
            setMetadataFetched(true);
        });
    };

    const handleSubmit = () => {
        if (!formRef.current?.check()) {
            return;
        }
        dispatch(setModalLoaderVisibility(true));
        templateDetails.apiMethod(emailTemplate).then(([result, status]) => {
            if (status === constants.statusCode.NoContent) {
                handleModalClose();
                refreshTemplates();
                toaster.push(
                    <Message type="success" showIcon>
                        {template(
                            t(
                                "deliverables.emailTemplates.creationSuccessMessage"
                            )
                        )({
                            templateName: emailTemplate.name,
                            actionName: templateDetails.actionName,
                        })}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            } else {
                const errorMessage = getErrorMessage(String(result), status);
                toaster.push(
                    <Message type="error" showIcon>
                        {errorMessage}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            }

            dispatch(setModalLoaderVisibility(false));
        });
    };

    const handleModalClose = () => {
        setEmailTemplate(templateDetails.emailTemplate);
        dispatch(setManageTemplateOpened(false));
        setMetadataFetched(false);
    };

    const handleFormChange = (formValue: IEmailTemplate) => {
        setEmailTemplate(formValue);
    };

    useEffect(() => {
        if (!isMetadataFetched && isOpened) {
            fetchMetadata();
        }
    }, [isMetadataFetched, isOpened]);

    useEffect(() => {
        setEmailTemplate(templateDetails.emailTemplate);
    }, [templateDetails.emailTemplate]);

    const variables =
        templateVariables &&
        templateVariables.map((item, index) => {
            return (
                <div key={index} className={css.variables}>
                    <p className={css.variableLabel}>{item.name}</p>
                    <Form.HelpText tooltip className={css.help}>
                        {item.description}
                    </Form.HelpText>
                </div>
            );
        });

    const header = <h1>{templateDetails.title}</h1>;
    const footer = (
        <ButtonToolbar className="custom-modal__button-toolbar">
            <Button
                type="submit"
                appearance="primary"
                onClick={handleSubmit}
                disabled={!isMetadataFetched}
            >
                {templateDetails.confirmButtonName}
            </Button>
            <Button
                appearance="ghost"
                onClick={handleModalClose}
                disabled={!isMetadataFetched}
            >
                {t("deliverables.emailTemplates.modal.cancel")}
            </Button>
        </ButtonToolbar>
    );

    const templatesLocalesData = locales.map((item) => {
        return { value: item, label: item };
    });

    const model = Schema.Model({
        name: Schema.Types.StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .minLength(
                constants.minNameLengthLimit,
                t("generalValidationMessages.minLength")
            )
            .maxLength(
                constants.maxLengthLimit,
                t("generalValidationMessages.maxLength5000")
            )
            .addRule((value) => {
                return existingTemplates
                    .filter((template) => template.id !== emailTemplate.id)
                    .every((template) => template.name !== value);
            }, t("generalValidationMessages.isUnique")),
        locale: Schema.Types.StringType().isRequired(
            t("generalValidationMessages.isRequired")
        ),
        subject: Schema.Types.StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .minLength(
                constants.minNameLengthLimit,
                t("generalValidationMessages.minLength")
            ),
        body: Schema.Types.StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .minLength(
                constants.minNameLengthLimit,
                t("generalValidationMessages.minLength")
            ),
    });

    return (
        <ModalContainer
            className={css.modal}
            backdrop="static"
            open={isOpened}
            onClose={handleModalClose}
            header={header}
            footer={footer}
        >
            <>
                <div className={css.modalContainer}>
                    <div className={css.formSection}>
                        <Form
                            className={css.form}
                            ref={formRef}
                            model={model}
                            formValue={emailTemplate}
                            onChange={(data: any) => {
                                handleFormChange(data);
                            }}
                        >
                            <Form.Group className={css.formGroup}>
                                <Form.ControlLabel>
                                    {t(
                                        "deliverables.emailTemplates.modal.nameTitle"
                                    )}
                                </Form.ControlLabel>
                                <Form.Control
                                    placeholder={t(
                                        "deliverables.emailTemplates.modal.namePlaceholder"
                                    )}
                                    name={"name"}
                                />
                            </Form.Group>
                            <Form.Group className={css.formGroup}>
                                <Form.ControlLabel>
                                    {t(
                                        "deliverables.emailTemplates.modal.localeTitle"
                                    )}
                                </Form.ControlLabel>
                                <Form.Control
                                    accepter={InputPicker}
                                    data={templatesLocalesData}
                                    placeholder={t(
                                        "deliverables.emailTemplates.modal.localePlaceholder"
                                    )}
                                    name={"locale"}
                                />
                            </Form.Group>
                            <Form.Group className={css.formGroup}>
                                <Form.ControlLabel>
                                    {t(
                                        "deliverables.emailTemplates.modal.subjectTitle"
                                    )}
                                </Form.ControlLabel>
                                <Form.Control
                                    placeholder={t(
                                        "deliverables.emailTemplates.modal.subjectPlaceholder"
                                    )}
                                    name={"subject"}
                                />
                            </Form.Group>
                            <Form.Group className={css.formGroup}>
                                <Form.ControlLabel>
                                    {t(
                                        "deliverables.emailTemplates.modal.bodyTitle"
                                    )}
                                </Form.ControlLabel>
                                <Form.Control
                                    accepter={Textarea}
                                    name={"body"}
                                    rows={10}
                                    placeholder={t(
                                        "deliverables.emailTemplates.modal.bodyPlaceholder"
                                    )}
                                />
                            </Form.Group>
                        </Form>
                    </div>
                    <div className={css.helpSection}>
                        <div className={css.variablesHeader}>
                            {t(
                                "deliverables.emailTemplates.modal.variablesHeader"
                            )}
                        </div>
                        {variables}
                    </div>
                </div>
            </>
        </ModalContainer>
    );
};

export default ManageEmailTemplate;
